'use client'
import React, { useState, useEffect } from 'react'
import InnerBlocks from '@/app/components/molecules/InnerBlocks'
import Section from '@/app/components/sections/Section'
import { TextWithIllustrationProps } from '~/app/types/TextWithIllustrationProps'
import Heading from '../atoms/Heading'
import Paragraph from '../atoms/Paragraph'
import Icon from '../atoms/Icons'
import { AnimatePresence, motion } from 'motion/react'
import { clean } from '~/app/utils/sanitizeString'

/**
 *
 * @returns: En sektion med tekst og illustration.
 * @example: <TextWithIllustration />
 * @alias: TextWithIllustration
 * @module: components/sections/TextWithIllustration
 * @summary: Denne komponent bruges til at vise en sektion med tekst og illustration.
 * @see: src/components/sections/TextWithIllustration.tsx
 * @version: 1.0.0
 * @property: [data]
 * @todo: implementer bedre håndtering af innerBlocks
 * @author: Emilie Hjøllund
 *
 **/

const FaneModul = ({ data }: TextWithIllustrationProps) => {
  // State to keep track of the currently active button
  const [activeButtonIndex, setActiveButtonIndex] = useState(0)

  // Automatically switch to the next item every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveButtonIndex((prevIndex) => (prevIndex + 1) % data.buttons.length)
    }, 15000) // 2000ms = 2 seconds

    // Cleanup the interval on component unmount
    return () => clearInterval(interval)
  }, [data.buttons.length])

  // Conditional border color logic
  const textColorClass =
    clean(data.design?.color?.color) === 'lys'
      ? 'text-DabraRød'
      : data.design?.color?.color === 'hvid'
        ? 'text-DabraRød'
        : 'text-lys'

  // Conditional border color logic
  const fillColorClass =
    clean(data.design?.color?.color) === 'lys'
      ? 'fill-DabraRød'
      : data.design?.color?.color === 'hvid'
        ? 'fill-DabraRød'
        : 'fill-lys'

  // Conditional border color logic
  const borderColorClass =
    clean(data.design?.color?.color) === 'lys'
      ? 'border-b-rødtekst/60'
      : data.design?.color?.color === 'hvid'
        ? 'border-b-rødtekst/60'
        : 'border-b-lys/60'

  return (
    <>
      <Section
        variant={clean(data.design?.color?.color)}
        paddingBottom={clean(data.design?.padding?.spacingBottom || 'default')}
        paddingTop={clean(data.design?.padding?.spacingTop || 'default')}
        className=""
      >
        <div className="grid col-span-full w-full md:w-[40%]">
          <Heading type="h2" tag="h2" className="">
            {data?.title}
          </Heading>
          <Paragraph>{data?.description}</Paragraph>
        </div>
        <div className="grid col-span-full md:grid-cols-2">
          <div className="flex flex-col justify-start space-y-8 items-start col-span-1 text-left  md:max-w-[65%]">
            <ul className="w-full">
              {data.buttons.map((button, index) => (
                <li className="w-full group" key={index}>
                  <button
                    onClick={() => setActiveButtonIndex(index)} // Update state on click
                    className={`h-20 w-full text-left border-b-2 ${borderColorClass} flex items-center transition-all duration-500 ease-in-out ${
                      activeButtonIndex === index
                        ? 'prose-headings:text-DabraRød' // Move the button and make text green when active
                        : 'prose-headings:text-rødtekst/50 group-hover:prose-headings:text-rød group-focus-within:prose-headings:text-rød' // Add hover and inactive styles
                    }`}
                  >
                    {data.icon}
                    {/* Icon (only visible when active) */}
                    {activeButtonIndex === index && (
                      <span
                        className={`${textColorClass} mr-4 transition-all duration-500 ease-in-out transform scale-100 opacity-100`}
                      >
                        <Icon
                          className={`${fillColorClass} size-8`}
                          type={button.icon}
                        />
                      </span>
                    )}

                    {/* Show icon only on hover for non-active buttons */}
                    {activeButtonIndex !== index && (
                      <span className="group-hover:w-auto basis-0 group-hover:basis-8 group-focus-within:basis-8 w-0 opacity-0 invisible group-hover:mr-4 text-rød group-hover:visible group-focus-within:visible transition-all duration-500 ease-in-out transform scale-90  group-hover:opacity-100 group-focus-within:opacity-100 group-hover:scale-100 group-focus-within:scale-100">
                        <Icon className="fill-rød size-8" type={button.icon} />
                      </span>
                    )}
                    <Heading
                      type="h5"
                      tag="h5"
                      className="m-0" // Keep the styles clean for Heading
                    >
                      {button.title}
                    </Heading>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1 pt-4">
            <AnimatePresence mode="popLayout">
              {data.buttons[activeButtonIndex] && (
                <motion.div
                  key={activeButtonIndex}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
                >
                  <InnerBlocks
                    blocks={data.buttons[activeButtonIndex].innerBlocks}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Section>
    </>
  )
}

export default FaneModul
